/*
 * ARANOX — Plataforma Digital Corporativa
 * ARX_BS_018.06.03.03 — USE CORPORATE ACCESS
 *
 * Hook reutilizável para consumir o Permission Backend por meio do
 * CorporatePermissionClient.
 *
 * Esta etapa não altera Sidebar, App.tsx, backend, matriz ou dependências.
 */

import {
  configApiRef,
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CorporatePermissionClient,
  corporateOrganizationIds,
  type CorporateOrganizationId,
} from './CorporatePermissionClient';

export interface CorporateAccessState {
  loaded: boolean;
  isAdmin: boolean;
  recognized: boolean;
  can: (organization: CorporateOrganizationId) => boolean;
  showCompanies: boolean;
  showPlatforms: boolean;
}

type OrganizationAccessMap = Readonly<
  Record<CorporateOrganizationId, boolean>
>;

const EMPTY_ACCESS: OrganizationAccessMap = Object.freeze({
  aranox: false,
  tes: false,
  agrion: false,
  sentrix: false,
  vitalis: false,
  cyrion: false,
  cyrius: false,
});

const COMPANY_ORGANIZATIONS: readonly CorporateOrganizationId[] = [
  'aranox',
  'tes',
  'agrion',
  'sentrix',
];

const PLATFORM_ORGANIZATIONS: readonly CorporateOrganizationId[] = [
  'vitalis',
  'cyrion',
  'cyrius',
];

export function useCorporateAccess(): CorporateAccessState {
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const client = useMemo(
    () =>
      new CorporatePermissionClient({
        configApi,
        discoveryApi,
        identityApi,
      }),
    [configApi, discoveryApi, identityApi],
  );

  const [loaded, setLoaded] = useState(false);
  const [organizationAccess, setOrganizationAccess] =
    useState<OrganizationAccessMap>(EMPTY_ACCESS);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let active = true;

    const loadAccess = async () => {
      setLoaded(false);

      try {
        const checks = [
          ...corporateOrganizationIds.map(organization => ({
            organization,
            module: 'catalog' as const,
            action: 'view' as const,
          })),
          {
            organization: 'aranox' as const,
            module: 'administration' as const,
            action: 'administer' as const,
          },
        ];

        const results = await client.authorizeMany(checks);

        if (!active) {
          return;
        }

        const nextAccess: Record<CorporateOrganizationId, boolean> = {
          ...EMPTY_ACCESS,
        };

        for (const result of results) {
          if (result.module === 'catalog' && result.action === 'view') {
            nextAccess[result.organization] = result.allowed;
          }
        }

        const administrationResult = results.find(
          result =>
            result.organization === 'aranox' &&
            result.module === 'administration' &&
            result.action === 'administer',
        );

        setOrganizationAccess(Object.freeze(nextAccess));
        setIsAdmin(administrationResult?.allowed === true);
      } catch {
        if (!active) {
          return;
        }

        // Política frontend restritiva: qualquer erro resulta em negação.
        setOrganizationAccess(EMPTY_ACCESS);
        setIsAdmin(false);
      } finally {
        if (active) {
          setLoaded(true);
        }
      }
    };

    void loadAccess();

    return () => {
      active = false;
    };
  }, [client]);

  const can = useCallback(
    (organization: CorporateOrganizationId): boolean =>
      loaded && organizationAccess[organization] === true,
    [loaded, organizationAccess],
  );

  const recognized =
    loaded &&
    (isAdmin ||
      corporateOrganizationIds.some(
        organization => organizationAccess[organization] === true,
      ));

  const showCompanies =
    loaded &&
    COMPANY_ORGANIZATIONS.some(
      organization => organizationAccess[organization] === true,
    );

  const showPlatforms =
    loaded &&
    PLATFORM_ORGANIZATIONS.some(
      organization => organizationAccess[organization] === true,
    );

  return {
    loaded,
    isAdmin: loaded && isAdmin,
    recognized,
    can,
    showCompanies,
    showPlatforms,
  };
}
